import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import PhygitalIcon from "@/components/PhygitalIcon";
import SharedFooter from "@/components/SharedFooter";
import {
  Sparkles,
  Eye,
  BarChart3,
  MapPin,
  Repeat,
  Layers,
  TrendingUp,
  Users,
  GraduationCap,
  ShoppingBag,
  Palette,
  Scissors,
  Heart,
  Hand,
  Check,
  ArrowRight,
  ArrowDown,
  Image as ImageIcon,
  ClipboardList,
  LineChart,
  Award,
  Megaphone,
  PackageCheck,
  Tag,
  ScrollText,
  Lock,
  Lightbulb,
} from "lucide-react";

/**
 * Brand Portal Sales Page (unlisted at /brands).
 * Black + neutral theme. No CTAs, no buttons. Static informational page only.
 * Vocabulary: no em-dashes, no "chip", "pill", "event" (in product copy), or "org".
 */
const BrandPortalSalesPage = () => {
  const [demoTab, setDemoTab] = useState("makeup");

  useEffect(() => {
    document.title = "phygital - For Beauty Brands";
  }, []);

  return (
    <div className="min-h-screen bg-white text-gray-900" data-testid="brand-portal-sales-page">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/85 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/home" className="flex items-center gap-2" data-testid="brand-portal-logo">
            <PhygitalIcon className="w-8 h-8" />
            <span className="font-semibold text-xl">phygital</span>
          </Link>
          <Badge className="bg-black text-white border-0 gap-1">
            <Sparkles className="w-3 h-3" />
            Brand Pilot
          </Badge>
        </div>
      </nav>

      {/* Hero */}
      <section className="pt-24 sm:pt-28 pb-12 sm:pb-16 px-4 relative overflow-hidden bg-white">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-50 via-white to-white" />
        <div className="relative max-w-5xl mx-auto text-center">
          <Badge className="mb-5 bg-black text-white border-0">
            <Sparkles className="w-3 h-3 mr-1" />
            Brand Portal Pilot by Phygital
          </Badge>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-5">
            Beauty Product Influence,
            <br />
            <span className="text-gray-500">Finally Measurable</span>
          </h1>
          <p className="text-base sm:text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
            Phygital helps beauty brands see how products move through services, artists, clients, and repeat
            demand. Products are discovered in the chair, in the kit, in the aftercare text, in the lesson,
            and through the provider a client already trusts.
          </p>

          {/* Value strip */}
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-3xl mx-auto text-left">
            {[
              { icon: Eye, text: "Aggregated insight only" },
              { icon: Users, text: "Provider attribution" },
              { icon: Lock, text: "No private preference cards shared" },
              { icon: BarChart3, text: "Real usage from real services" },
            ].map((item, i) => (
              <div
                key={i}
                className="flex items-start gap-2.5 p-3 rounded-xl border border-gray-200 bg-white"
                data-testid={`brand-portal-value-${i}`}
              >
                <item.icon className="w-4 h-4 text-black mt-0.5 flex-shrink-0" />
                <span className="text-sm text-gray-800">{item.text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The beauty problem */}
      <section className="py-14 sm:py-20 px-4 bg-gray-50 border-y border-gray-100">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <Badge variant="outline" className="mb-4 border-gray-300 text-gray-700">
              The beauty problem
            </Badge>
            <h2 className="text-2xl sm:text-4xl font-bold mb-4">
              Influence happens in the chair. The brand never sees it.
            </h2>
            <p className="text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Beauty brands already know influencers matter. But service providers are influencing purchase
              decisions every day, and most of that influence is invisible. A product can be used on 40
              clients, recommended after every appointment, appear in final look content, and drive repeat
              demand, and the brand still does not know which provider created that demand or what service
              moment made the product matter.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              {
                icon: Palette,
                title: "Makeup artists",
                copy: "choose complexion, lip, skin prep, lashes, and setting sprays.",
              },
              {
                icon: Scissors,
                title: "Hairstylists",
                copy: "recommend treatments, styling product, color care, edge control, and routines.",
              },
              {
                icon: Heart,
                title: "Lash artists",
                copy: "influence curl, length, adhesives, serums, aftercare, and mapping.",
              },
              {
                icon: Hand,
                title: "Nail techs",
                copy: "drive demand around colors, shapes, gels, press ons, charms, and care.",
              },
            ].map((b, i) => (
              <Card key={i} className="border-gray-200 bg-white" data-testid={`provider-influence-${i}`}>
                <CardContent className="p-5 flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-black flex items-center justify-center flex-shrink-0">
                    <b.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm">
                      <span className="font-semibold text-gray-900">{b.title}</span>{" "}
                      <span className="text-gray-700">{b.copy}</span>
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What you see - phone mockups */}
      <section className="py-14 sm:py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-black text-white border-0">
              <Eye className="w-3 h-3 mr-1" />
              What you see
            </Badge>
            <h2 className="text-2xl sm:text-4xl font-bold mb-3">
              Clients see their products. Brands see the demand.
            </h2>
            <p className="text-gray-700 max-w-2xl mx-auto">
              Clients see their products, shades, and samples on the confirmation page. Brands see
              aggregated usage and demand, never the private preference card.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            {/* Phone A - Client confirmation */}
            <div className="flex justify-center">
              <PhoneMockup label="Client confirmation">
                <div className="p-4 space-y-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-gray-500">
                      Bridal trial with Shelsea
                    </p>
                    <p className="text-sm font-semibold text-gray-900">Saturday, 9:00 AM</p>
                  </div>
                  <div className="rounded-xl bg-gray-50 border border-gray-200 p-3 space-y-2.5">
                    <p className="text-[11px] font-semibold text-gray-900">Products for your look</p>
                    {[
                      { brand: "FENTY", name: "Pro Filt'r Foundation", shade: "NC42", dot: "#a87257" },
                      { brand: "CHARLOTTE T.", name: "Pillow Talk Lip", shade: "Original", dot: "#c97b7b" },
                      { brand: "URBAN DECAY", name: "All Nighter Setting Spray", shade: "Long lasting", dot: "#e8e8e8" },
                    ].map((p, i) => (
                      <div key={i} className="flex items-center gap-2.5">
                        <span
                          className="w-7 h-7 rounded-full border border-gray-200"
                          style={{ backgroundColor: p.dot }}
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-[9px] uppercase tracking-wide text-gray-500">{p.brand}</p>
                          <p className="text-[11px] font-medium text-gray-900 truncate">{p.name}</p>
                        </div>
                        <span className="text-[10px] text-gray-600 whitespace-nowrap">{p.shade}</span>
                      </div>
                    ))}
                  </div>
                  <div className="rounded-xl bg-gray-50 border border-gray-200 p-3">
                    <p className="text-[11px] font-semibold text-gray-900 mb-1.5">Lash look</p>
                    <p className="text-[11px] text-gray-700">Wispy natural, individual mink</p>
                  </div>
                  <div className="rounded-xl bg-gray-50 border border-gray-200 p-3">
                    <p className="text-[11px] font-semibold text-gray-900 mb-1.5">Samples on the way</p>
                    <p className="text-[11px] text-gray-700">Setting spray travel size, lip oil sachet</p>
                  </div>
                </div>
              </PhoneMockup>
            </div>

            {/* Phone B - Brand portal */}
            <div className="flex justify-center">
              <PhoneMockup label="Brand portal">
                <div className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <p className="text-[10px] uppercase tracking-wider text-gray-500">This month</p>
                    <Badge className="bg-black text-white border-0 text-[9px] py-0">Aggregated</Badge>
                  </div>
                  <div className="space-y-2">
                    {[
                      { label: "Foundation NC42", pct: 84 },
                      { label: "Pillow Talk Lip", pct: 61 },
                      { label: "Setting Spray", pct: 47 },
                      { label: "Wispy Mink Lash", pct: 38 },
                    ].map((row, i) => (
                      <div key={i}>
                        <div className="flex items-center justify-between text-[10px] text-gray-700 mb-1">
                          <span className="truncate">{row.label}</span>
                          <span className="font-semibold text-gray-900">{row.pct}%</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-gray-100">
                          <div
                            className="h-1.5 rounded-full bg-black"
                            style={{ width: `${row.pct}%` }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-2 pt-1">
                    <Stat label="Providers" value="14" />
                    <Stat label="Cities" value="6" />
                    <Stat label="Confirm clicks" value="312" />
                    <Stat label="Repeat usage" value="58%" />
                  </div>
                </div>
              </PhoneMockup>
            </div>
          </div>
        </div>
      </section>

      {/* Provider as influencer - tabs demo */}
      <section className="py-14 sm:py-20 px-4 bg-gray-50 border-y border-gray-100">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <Badge variant="outline" className="mb-4 border-gray-300 text-gray-700">
              Provider as influencer
            </Badge>
            <h2 className="text-2xl sm:text-4xl font-bold mb-3">
              Influence inside the service relationship.
            </h2>
            <p className="text-gray-700 max-w-2xl mx-auto">
              Phygital measures the moment between the kit and the client.
            </p>
          </div>

          <Tabs value={demoTab} onValueChange={setDemoTab}>
            <TabsList className="grid grid-cols-3 max-w-md mx-auto mb-8 bg-white border border-gray-200">
              <TabsTrigger value="makeup" data-testid="brand-tab-makeup">
                Makeup
              </TabsTrigger>
              <TabsTrigger value="hair" data-testid="brand-tab-hair">
                Hair
              </TabsTrigger>
              <TabsTrigger value="lashnail" data-testid="brand-tab-lashnail">
                Lash + Nail
              </TabsTrigger>
            </TabsList>

            <TabsContent value="makeup">
              <FlowCard
                heading="Foundation in a bridal trial"
                steps={[
                  "A makeup artist uses a foundation during a bridal trial.",
                  "The product is linked to the client's final look.",
                  "The client receives a confirmation with the product list and shade.",
                  "The client clicks the product link.",
                  "The artist reuses the product on future appointments.",
                  "The brand sees usage, clicks, repeat patterns, service type, and provider performance.",
                ]}
              />
            </TabsContent>

            <TabsContent value="hair">
              <FlowCard
                heading="Wig install with wig glue"
                steps={[
                  "A stylist performs a wig install and uses a specific wig glue.",
                  "The glue is linked to the service notes and aftercare.",
                  "The client receives a confirmation with the wig glue, hold level, and removal product.",
                  "The client clicks or saves the recommended wig glue.",
                  "The stylist uses that glue across future installs.",
                  "The brand sees which services drive product interest and which stylists create repeat demand.",
                ]}
              />
            </TabsContent>

            <TabsContent value="lashnail">
              <FlowCard
                heading="Style, color, and aftercare"
                steps={[
                  "A lash or nail artist links products, styles, colors, shapes, or aftercare to the service.",
                  "The product list lives on the confirmation page with shade or curl detail.",
                  "The client receives follow up recommendations.",
                  "The brand sees what styles are trending, which providers are driving the interest, and which categories repeat.",
                ]}
              />
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Analytics grid - what the portal can show */}
      <section className="py-14 sm:py-20 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <Badge className="mb-4 bg-black text-white border-0">
              <BarChart3 className="w-3 h-3 mr-1" />
              What the brand portal can show
            </Badge>
            <h2 className="text-2xl sm:text-4xl font-bold mb-3">Aggregated insight, no private data.</h2>
            <p className="text-gray-700 max-w-2xl mx-auto">
              Brands never receive private client preference cards. The value is aggregated product usage,
              provider attribution, and beauty demand insight.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { icon: Users, title: "Product usage by provider", copy: "Who is using what, how often." },
              { icon: Layers, title: "Usage by service category", copy: "Which services move which products." },
              { icon: Sparkles, title: "Top products in final looks", copy: "Bridal, editorial, everyday, gala." },
              { icon: ClipboardList, title: "Top products on preference cards", copy: "Aggregated counts, never the card itself." },
              { icon: Repeat, title: "Recommended after appointments", copy: "Aftercare and follow up product flow." },
              { icon: ArrowRight, title: "Clicks from confirmation pages", copy: "Real intent in the buying moment." },
              { icon: TrendingUp, title: "Repeat product usage", copy: "The signal of true demand." },
              { icon: Palette, title: "Shade and color demand", copy: "Where the curve is shifting." },
              { icon: LineChart, title: "Style and trend movement", copy: "Inspo turning into actual service." },
              { icon: MapPin, title: "Top providers, cities, service types", copy: "Where traction is forming." },
              { icon: GraduationCap, title: "Products used in lessons and classes", copy: "Adoption inside education." },
              { icon: Heart, title: "Repeat bookings and follow up interest", copy: "Products that hold a client." },
            ].map((c, i) => (
              <Card key={i} className="border-gray-200 hover:border-black transition-colors" data-testid={`analytics-card-${i}`}>
                <CardContent className="p-5">
                  <div className="w-9 h-9 rounded-lg bg-black flex items-center justify-center mb-3">
                    <c.icon className="w-4 h-4 text-white" />
                  </div>
                  <p className="text-sm font-semibold text-gray-900 mb-1">{c.title}</p>
                  <p className="text-xs text-gray-600 leading-relaxed">{c.copy}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Preference cards are shareable */}
      <section className="py-14 sm:py-20 px-4 bg-black text-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <Badge className="mb-4 bg-white text-black border-0">
              <ScrollText className="w-3 h-3 mr-1" />
              Preference cards
            </Badge>
            <h2 className="text-2xl sm:text-4xl font-bold mb-3">
              Preference cards travel with the client, not the brand.
            </h2>
            <p className="text-gray-300 max-w-2xl mx-auto">
              The client owns the card. Providers see what they need to prep. Brands see counts only.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div className="flex justify-center">
              <PhoneMockup label="Preference card" dark>
                <div className="p-4 space-y-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-gray-500">Shareable card</p>
                    <p className="text-sm font-semibold text-gray-900">Bridal glam, soft and dewy</p>
                  </div>
                  <div className="rounded-xl bg-gray-50 border border-gray-200 p-3 space-y-2">
                    <p className="text-[11px] font-semibold text-gray-900">Linked products</p>
                    {[
                      { brand: "FENTY", name: "Foundation", shade: "NC42", dot: "#a87257" },
                      { brand: "NARS", name: "Concealer", shade: "Custard", dot: "#d3a87b" },
                      { brand: "CHARLOTTE T.", name: "Lip", shade: "Pillow Talk", dot: "#c97b7b" },
                      { brand: "LANEIGE", name: "Lip prep", shade: "Original", dot: "#f4d3d3" },
                    ].map((p, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <span
                          className="w-5 h-5 rounded-full border border-gray-200"
                          style={{ backgroundColor: p.dot }}
                        />
                        <p className="text-[10px] text-gray-700 flex-1 truncate">
                          <span className="font-semibold text-gray-900">{p.brand}</span> {p.name}
                        </p>
                        <span className="text-[10px] text-gray-500">{p.shade}</span>
                      </div>
                    ))}
                  </div>
                  <div className="rounded-xl bg-gray-50 border border-gray-200 p-3">
                    <p className="text-[11px] font-semibold text-gray-900 mb-1">Sample requests</p>
                    <p className="text-[10px] text-gray-700">Lip oil, primer, setting spray</p>
                  </div>
                </div>
              </PhoneMockup>
            </div>

            <div className="space-y-4">
              {[
                "The client owns the card and shares it with the provider.",
                "The provider sees linked products and shades to prep the kit.",
                "The brand sees aggregated counts only. Never names. Never the card.",
                "The client can re-share to a new provider, a new city, a new look.",
              ].map((line, i) => (
                <div key={i} className="flex items-start gap-3" data-testid={`pref-card-bullet-${i}`}>
                  <div className="w-6 h-6 rounded-full bg-white text-black flex items-center justify-center flex-shrink-0">
                    <Check className="w-3.5 h-3.5" />
                  </div>
                  <p className="text-base text-gray-100 leading-relaxed">{line}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Dead end expansion */}
      <section className="py-14 sm:py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <Badge variant="outline" className="mb-4 border-gray-300 text-gray-700">
              Where the dead end used to stop
            </Badge>
            <h2 className="text-2xl sm:text-4xl font-bold mb-3">
              From purchase to completed service and look.
            </h2>
            <p className="text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Most brand analytics stop at purchase. Phygital extends visibility through the service, the
              final look, the aftercare text, and the repeat, without exposing any private client data.
            </p>
          </div>

          <div className="space-y-6">
            {/* Old way */}
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-500 mb-3">Old way</p>
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <FlowNode label="Discovery" />
                <ArrowRight className="w-4 h-4 text-gray-400" />
                <FlowNode label="Purchase" />
                <ArrowRight className="w-4 h-4 text-gray-400" />
                <FlowNode label="Dead end" muted />
              </div>
            </div>

            {/* Phygital way */}
            <div>
              <p className="text-xs uppercase tracking-wider text-gray-500 mb-3">Phygital way</p>
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <FlowNode label="Discovery" />
                <ArrowRight className="w-4 h-4 text-gray-400" />
                <FlowNode label="Purchase" />
                <ArrowRight className="w-4 h-4 text-gray-400" />
                <FlowNode label="Service" active />
                <ArrowRight className="w-4 h-4 text-gray-400" />
                <FlowNode label="Final look" active />
                <ArrowRight className="w-4 h-4 text-gray-400" />
                <FlowNode label="Aftercare" active />
                <ArrowRight className="w-4 h-4 text-gray-400" />
                <FlowNode label="Repeat" active />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Instructor add-on */}
      <section className="py-14 sm:py-20 px-4 bg-gray-50 border-y border-gray-100">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <Badge className="mb-4 bg-black text-white border-0">
              <GraduationCap className="w-3 h-3 mr-1" />
              Instructor add on
            </Badge>
            <h2 className="text-2xl sm:text-4xl font-bold mb-3">
              For academies, pro programs, and ambassador networks.
            </h2>
            <p className="text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Add an instructor surface for product training, certification, masterclasses, retail associate
              training, ambassador onboarding, and beauty school partnerships.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card className="border-gray-200 bg-white">
              <CardContent className="p-6">
                <p className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Lightbulb className="w-4 h-4" /> Use cases
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    "Artist education",
                    "Product training",
                    "Pro certification",
                    "Masterclasses",
                    "Retail associate training",
                    "Ambassador onboarding",
                    "Beauty school partnerships",
                    "Virtual or in person workshops",
                    "New product education",
                    "Technique based classes",
                  ].map((u, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                      <Check className="w-3.5 h-3.5 mt-1 flex-shrink-0 text-black" />
                      <span>{u}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="border-gray-200 bg-white">
              <CardContent className="p-6">
                <p className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <BarChart3 className="w-4 h-4" /> The instructor dashboard tracks
                </p>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    "Student RSVPs",
                    "Skill level",
                    "Learning goals",
                    "Products used in class",
                    "Recommended kits",
                    "Class attendance",
                    "Follow up resources",
                    "Product clicks",
                    "Repeat class interest",
                    "Provider progression",
                    "Top product questions",
                    "Lessons that drive product adoption",
                  ].map((u, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700">
                      <Check className="w-3.5 h-3.5 mt-1 flex-shrink-0 text-black" />
                      <span>{u}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>

          <p className="text-center text-sm text-gray-600 mt-6 max-w-3xl mx-auto">
            This connects education, product usage, and provider influence in one system.
          </p>
        </div>
      </section>

      {/* Questions brands can answer */}
      <section className="py-14 sm:py-20 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <Badge variant="outline" className="mb-4 border-gray-300 text-gray-700">
              Why beauty brands would care
            </Badge>
            <h2 className="text-2xl sm:text-4xl font-bold mb-3">The questions Phygital can answer.</h2>
            <p className="text-gray-700 max-w-2xl mx-auto">
              Insight that supports launches, pro programs, retail strategy, education, ambassador
              campaigns, and creator partnerships.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              ["Which products are artists actually using?", "Real usage data, not declared."],
              ["Which shades, colors, or formulas are showing demand?", "Trends straight from the chair."],
              ["Which providers are driving product interest?", "Attribution by name."],
              ["Which service types create the most engagement?", "Bridal, editorial, color, lash, nail."],
              ["Which classes lead to product clicks or repeat usage?", "Education return on investment."],
              ["Which products are recommended after appointments?", "Aftercare influence."],
              ["Which markets are showing early traction?", "City level demand."],
              ["Which trends are moving from inspo into actual services?", "Saved look to real chair."],
            ].map(([q, a], i) => (
              <Card key={i} className="border-gray-200" data-testid={`brand-question-${i}`}>
                <CardContent className="p-5">
                  <p className="text-sm font-semibold text-gray-900 mb-1">{q}</p>
                  <p className="text-xs text-gray-600">{a}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Future brand pilot structure */}
      <section className="py-14 sm:py-20 px-4 bg-gray-50 border-y border-gray-100">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <Badge className="mb-4 bg-black text-white border-0">
              <PackageCheck className="w-3 h-3 mr-1" />
              Future brand pilot structure
            </Badge>
            <h2 className="text-2xl sm:text-4xl font-bold mb-3">
              What a brand pilot can include.
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { icon: ShoppingBag, label: "Approved product catalog" },
              { icon: Tag, label: "Provider product tagging" },
              { icon: ImageIcon, label: "Final look product tracking" },
              { icon: Repeat, label: "Post service product recommendations" },
              { icon: ArrowRight, label: "Purchase links or campaign links" },
              { icon: Users, label: "Provider attribution codes" },
              { icon: BarChart3, label: "Aggregated usage dashboard" },
              { icon: LineChart, label: "Monthly insight reports" },
              { icon: Award, label: "Top provider reporting" },
              { icon: TrendingUp, label: "Product demand insights" },
              { icon: GraduationCap, label: "Instructor or academy add on" },
              { icon: Megaphone, label: "Campaign performance summary" },
            ].map((item, i) => (
              <div
                key={i}
                className="flex items-center gap-3 p-4 rounded-xl bg-white border border-gray-200"
                data-testid={`pilot-structure-${i}`}
              >
                <div className="w-8 h-8 rounded-lg bg-black flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-4 h-4 text-white" />
                </div>
                <p className="text-sm text-gray-800">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* The big idea */}
      <section className="py-16 sm:py-24 px-4 bg-black text-white">
        <div className="max-w-3xl mx-auto text-center">
          <Badge className="mb-5 bg-white text-black border-0">The big idea</Badge>
          <h2 className="text-3xl sm:text-5xl font-bold leading-tight mb-6">
            Phygital makes invisible influence visible.
          </h2>
          <p className="text-lg text-gray-300 leading-relaxed">
            Beauty providers are already influencing what clients trust, try, buy, and return to. The brand
            portal turns service providers into measurable partners and product usage into insight.
          </p>
        </div>
      </section>

      <SharedFooter />
    </div>
  );
};

/* Helpers */

const PhoneMockup = ({ children, label, dark }) => (
  <div className="relative">
    <div className={`w-[300px] rounded-[2.5rem] p-3 shadow-2xl ${dark ? "bg-gray-900" : "bg-gray-900"}`}>
      <div className="bg-white rounded-[2rem] overflow-hidden">
        <div className="bg-gray-100 px-4 py-2 flex justify-between items-center text-[11px] text-gray-700">
          <span>9:41</span>
          <span className="font-medium">{label}</span>
        </div>
        {children}
      </div>
    </div>
  </div>
);

const Stat = ({ label, value }) => (
  <div className="rounded-lg border border-gray-200 bg-white p-2">
    <p className="text-[9px] uppercase tracking-wide text-gray-500">{label}</p>
    <p className="text-sm font-semibold text-gray-900">{value}</p>
  </div>
);

const FlowCard = ({ heading, steps }) => (
  <Card className="border-gray-200 bg-white">
    <CardContent className="p-6 sm:p-8">
      <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Example flow</p>
      <p className="text-lg font-semibold text-gray-900 mb-5">{heading}</p>
      <ol className="space-y-3">
        {steps.map((s, i) => (
          <li key={i} className="flex items-start gap-3" data-testid={`flow-step-${i}`}>
            <span className="w-6 h-6 rounded-full bg-black text-white text-[11px] font-semibold flex items-center justify-center flex-shrink-0 mt-0.5">
              {i + 1}
            </span>
            <span className="text-sm text-gray-800 leading-relaxed">{s}</span>
          </li>
        ))}
      </ol>
    </CardContent>
  </Card>
);

const FlowNode = ({ label, active, muted }) => {
  const base =
    "px-3 sm:px-4 py-2 rounded-full text-xs sm:text-sm font-medium border whitespace-nowrap";
  if (muted) return <span className={`${base} bg-gray-100 text-gray-400 border-gray-200 line-through`}>{label}</span>;
  if (active) return <span className={`${base} bg-black text-white border-black`}>{label}</span>;
  return <span className={`${base} bg-white text-gray-800 border-gray-300`}>{label}</span>;
};

export default BrandPortalSalesPage;
